import React, {useState} from 'react';
import {Button, Card, Form, Input, message} from 'antd';
import {LockOutlined, UserOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {UsernatorClient} from "../generated/user_grpc_web_pb";
import {LoginRequest} from "../generated/user_pb";
import GRPC_BACKEND_URL, {GRPC_CLIENT_OPTIONS, REGISTRATION_ENABLED, SSO_ENABLED} from "../grpcConfig";
import AnimatedLogo from "../Components/AnimatedLogo";
import {hashStringSHA256, isNetworkError} from "@dowty/core";
import {AuthLayout} from '@dowty/ui';
import RouterLegalFooter from '../Components/RouterLegalFooter';
import {isGerman} from '../utils/language';
import {encodeMessage} from '../offline';

interface LoginPageProps {
    onLogin: () => void;
    onRetry?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({onLogin, onRetry}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const client = new UsernatorClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);
    async function loginMethod(username: string, password: string) {
        const user = new LoginRequest();
        user.setUsername(username);

        let hashedPw: string;
        try {
            hashedPw = await hashStringSHA256(password);
        } catch {
            message.error(isGerman() ? 'Anmeldung konnte nicht vorbereitet werden. Bitte erneut versuchen.' : 'Could not prepare sign-in. Please try again.');
            return false;
        }
        user.setPassword(hashedPw);

        return new Promise<boolean>((resolve, reject) => {
            client.login(user, undefined, (err, res) => {
                if (err) { reject(err); return; }
                if (!res) { reject(new Error('Empty login response')); return; }
                localStorage.setItem('user', encodeMessage(res));
                resolve(true);
            })
        })
    }

    async function onLoginFormSubmit(values: { username: string; password: string }) {
        setIsSubmitting(true);

        try {
            if (await loginMethod(values.username.trim(), values.password)) {
                onLogin();
                navigate('/dashboard');
            }
        } catch (error) {
            if (isNetworkError(error)) message.error(isGerman() ? 'Anmeldung momentan nicht möglich. Bitte Verbindung prüfen.' : 'Sign-in is unavailable. Check your connection and try again.');
            else message.error(isGerman() ? 'Benutzername oder Passwort ungültig.' : 'Invalid username or password.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return <>
        <AuthLayout title="SportDings" description="Sign in to track your activities">
        <AnimatedLogo collapsed={false} loginPage />
        <Card>
                    {SSO_ENABLED && sessionStorage.getItem('dowtex-sso-interactive') === '1' && <p>
                        <output>{isGerman() ? 'Melde dich erneut an, um fortzufahren. Gespeicherte Aktivitäten werden danach synchronisiert.' : 'Sign in again to continue. Saved activities will sync after you sign in.'}</output>
                        <Button type="link" onClick={onRetry}>{isGerman() ? 'Erneut versuchen' : 'Try again'}</Button>
                    </p>}
                    <Form onFinish={onLoginFormSubmit} layout="vertical">
                        <Form.Item
                            name="username"
                            label="Username"
                            rules={[{ required: true, whitespace: true, message: isGerman() ? 'Bitte Benutzernamen eingeben.' : 'Please input your username!' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Username" autoComplete="username" />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[{ required: true, message: 'Please input your password!' }]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Password" autoComplete="current-password" />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 8 }}>
                            <Button type="primary" size="large" htmlType="submit" block style={{minHeight: 44}} loading={isSubmitting}>
                                Log in
                            </Button>
                        </Form.Item>

                        {REGISTRATION_ENABLED && <Form.Item style={{ marginBottom: 0 }}>
                            <Button type="link" size="large" block style={{minHeight: 44}} onClick={() => navigate('/register')}>
                                Don't have an account? Register here
                            </Button>
                        </Form.Item>}
                    </Form>
        </Card>
        </AuthLayout>
        <RouterLegalFooter />
    </>;
};

export default LoginPage;
